<script lang="ts" setup>
import { WidgetContext } from "@/shared/environment/widgets/widget-context";
import { computed } from "vue";
import QitRouterLink from "@/shared/components/router-link/qit-router-link.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import WidgetHeader from "@/shared/components/widgets/widget-header.vue";
import { useDocumentSearch } from "@/abilities/documentation/composables/document-search/document-search.ts";
import { getSearchContextByWidgetContext } from "@/shared/utils/widget-utils.ts";
import DocumentListCardSmall from "@/shared/documentation/document-list-card-small.vue";
import { useDocumentRoute } from "@/shared/router/document-route.ts";
import { useDocQueryParams } from "@/shared/composables/doc-query-params.ts";

const props = defineProps<{
    widgetContext: WidgetContext;
}>();

const { getPathToDocumentations } = useDocumentRoute();
const { isLessThanMedium } = useScreenSize();
const { queryParams } = useDocQueryParams();

const searchContext = computed(() => {
    return getSearchContextByWidgetContext(props.widgetContext);
});

const { contents, loading } = useDocumentSearch({
    searchContext,
    showFirstRelevantPropertyItemOnly: true,
    count: 6,
});

const isWidgetVisible = computed(() => {
    return !loading.value && contents.value && contents.value.length > 0;
});
</script>

<template>
    <div v-if="isWidgetVisible" id="relatedDocs">
        <WidgetHeader :title="$t('documentation.Documentation')" icon="fa-regular fa-book" class="q-mb-md">
            <template #right>
                <QitRouterLink
                    data-qs="related-documents-view-all-documents-btn"
                    :label="$t('shared.View all documents')"
                    :to="getPathToDocumentations(searchContext, queryParams)"
                    hover-background
                />
            </template>
        </WidgetHeader>
        <div
            data-qs="related-documents-widget"
            class="row"
            :class="{ 'q-col-gutter-md': !isLessThanMedium, gutter: isLessThanMedium }"
        >
            <div class="col-md-4 col-xs-12 flex" v-for="(item, index) in contents" :key="item.contentId">
                <DocumentListCardSmall
                    class="items-stretch flex-grow"
                    :content="item"
                    :context="searchContext"
                    :test-id="`related-document-${index}`"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.gutter {
    gap: 12px;
}
</style>
